import React from 'react';
import { reportFeError } from '../utils/feErrorReporting';

/**
 * App-root error boundary (CEL-1117, Arjun 2026-08-11).
 *
 * Two jobs:
 *  1. Report React render/lifecycle crashes to the FE error lane (the
 *     componentStack is gold for locating minified crashes).
 *  2. Replace the white-screen-of-death with a graceful recovery screen.
 *
 * Deliberately self-contained: inline styles only (no App.css / antd — if the
 * app tree crashed, its CSS pipeline may be part of the crash), theme read
 * directly off <html data-theme> which GlobalLayout/index.html set pre-paint.
 */
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const componentStack = ((info && info.componentStack) || '').slice(0, 400);
    reportFeError(
      'react_boundary',
      (error && error.message) || 'React render error',
      `${(error && error.stack) || ''}\n[componentStack]${componentStack}`
    );
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDark =
      typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-theme') === 'dark';
    const bg = isDark ? '#161618' : '#ffffff';
    const text = isDark ? '#f2f2f7' : '#1d1d1f';
    const muted = isDark ? '#a1a1a6' : '#6e6e73';
    const primary = isDark ? '#7dc3c3' : '#2e7e7e';

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: bg,
          color: text,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: '24px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚡</div>
        <h1 style={{ fontSize: '22px', fontWeight: 600, margin: '0 0 8px' }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: '15px', color: muted, margin: '0 0 24px', maxWidth: '420px' }}>
          The page hit an unexpected error. It's been reported automatically —
          reloading usually fixes it.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: primary,
            color: isDark ? '#161618' : '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 24px',
            fontSize: '15px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default RootErrorBoundary;
